import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { RunStatus } from '../../../../../../../../../backend/src/types/types';
import { FailedDocsModal } from '../components/modals/failed-docs-modal';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useUpdateRun } from '@/api/mutations/useUpdateRun';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { Run } from '@/api/types/backend-dto-types';
import formatDate from '@/helpers/formatDate';
import { getRunType } from '@/helpers/runs';
import { runNameSchema, RunNameFormData } from '@/new/components/dialogs/run-name-schema';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/new/components/shadcn/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TableNullField } from '@/new/components/table/table-null-field';
import { TablePagination } from '@/new/components/table/table-pagination';

const getStatusDisplay = (run: Run) => {
  if (run.status === RunStatus.AwaitingApproval) {
    return {
      text: 'Awaiting Approval',
      icon: <Icon className="lucide--shield-alert size-3.5 text-amber-600" />,
    };
  }
  if (run.status === RunStatus.Failed) {
    return {
      text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Failed',
      icon: <Icon className="lucide--x-circle size-3.5 text-red-500" />,
    };
  }
  if (run.completedAt) {
    const status = run.status?.toLowerCase() || '';
    if (
      status.includes('fail') ||
      (run.failedPagesCount && run.failedPagesCount > 0 && run.totalPages === run.failedPagesCount)
    ) {
      return {
        text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Failed',
        icon: <Icon className="lucide--x-circle size-3.5 text-red-500" />,
      };
    }
    return {
      text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Completed',
      icon: <Icon className="lucide--check-circle size-3.5 text-green-500" />,
    };
  }

  if (run.inQueue) {
    const queuePosition = run.queuePosition !== null ? `, position ${run.queuePosition + 1}` : '';
    return {
      text: `In queue${queuePosition}`,
      icon: <Icon className="lucide--loader-circle size-3.5 animate-spin text-amber-500 duration-700" />,
    };
  }
  return {
    text: run.status ? run.status.charAt(0).toUpperCase() + run.status.slice(1) : 'Processing',
    icon: <Icon className="lucide--loader-circle size-3.5 animate-spin text-amber-500 duration-700" />,
  };
};

const formatNumber = (num: number) => {
  return num.toLocaleString();
};

const columnHelper = createColumnHelper<Run>();

export function RunLog() {
  const { matterId } = useParams();
  if (!matterId) return null;
  const { data: runs, isLoading, refetch } = useGetMatterRuns(matterId);
  const { mutateAsync: updateRunName, isLoading: isUpdatingName } = useUpdateRun();
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [editingRun, setEditingRun] = useState<{ id: string; name: string | null } | null>(null);

  const runNameForm = useForm<RunNameFormData>({
    resolver: zodResolver(runNameSchema),
    mode: 'onTouched',
    defaultValues: { runName: '' },
  });

  const handleEditName = (run: Run) => {
    setEditingRun({ id: run.id, name: run.name });
    runNameForm.reset({ runName: run.name ?? '' });
  };

  const handleSaveName = async (data: RunNameFormData) => {
    if (!editingRun) return;
    const trimmed = data.runName.trim();
    await updateRunName({ runId: editingRun.id, name: trimmed });
    toast.success('Run name updated');
    trackCustomEvent(TrackingEventsV2.RunNameEdit, { matterId });
    setEditingRun(null);
    runNameForm.reset();
    refetch();
  };

  const columns = [
    columnHelper.accessor('createdDate', {
      header: () => <span>Date</span>,
      cell: (info) => (
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Icon className="lucide--clock h-3 w-3" />
          <span>{formatDate(info.getValue(), true, true)}</span>
        </div>
      ),
    }),
    columnHelper.accessor('name', {
      header: () => <span>Name</span>,
      cell: (info) => {
        const name = info.getValue();
        return (
          <div className="flex items-center gap-1.5">
            {name ? (
              <Tooltip>
                <TooltipTrigger className="max-w-32 truncate text-xs text-gray-800">{name}</TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{name}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="text-xs text-gray-400">Unnamed</span>
            )}
            <button
              onClick={() => handleEditName(info.row.original)}
              className="text-gray-400 transition-colors hover:text-gray-500"
            >
              <Icon className="lucide--pencil-line size-3" />
            </button>
          </div>
        );
      },
    }),
    columnHelper.accessor('type', {
      header: () => <span>Type</span>,
      cell: (info) => <p className="text-xs text-gray-800">{getRunType(info.row.original, !info.row.original.case)}</p>,
    }),
    columnHelper.display({
      id: 'status',
      header: () => <span>Status</span>,
      cell: (info) => {
        const statusDisplay = getStatusDisplay(info.row.original);
        return (
          <div className="flex items-center gap-2">
            {statusDisplay.icon}
            <span className="text-xs">{statusDisplay.text}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor('case', {
      header: () => <span>Subset</span>,
      cell: (info) => {
        const caseData = info.getValue();
        return caseData ? (
          <Tooltip>
            <TooltipTrigger className="max-w-36 truncate text-xs">{caseData.name}</TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{caseData.name}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-xs text-gray-400">All Documents</span>
        );
      },
    }),
    columnHelper.accessor('createdBy.email', {
      header: () => <span>Submitted By</span>,
      cell: (info) => {
        const email = info.getValue();
        return email ? <span className="text-xs text-gray-900">{email}</span> : <TableNullField />;
      },
    }),

    columnHelper.accessor('successfulPagesCount', {
      header: () => <span>Processed</span>,
      cell: (info) => {
        const count = info.getValue();
        return count !== undefined && count > 0 ? (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="h-1.5 w-1.5 rounded-full bg-green-700" />
            <span className="font-medium text-green-700">{formatNumber(count)} pages</span>
          </div>
        ) : (
          <TableNullField />
        );
      },
    }),
    columnHelper.accessor('failedPagesCount', {
      header: () => <span>Failed</span>,
      cell: (info) => {
        const count = info.getValue();
        return count > 0 ? (
          <Button
            variant="destructive"
            size="sm"
            className="group text-xs"
            onClick={() => {
              trackCustomEvent(TrackingEventsV2.FileHistoryClickFailedPages, {
                matterId,
                runId: info.row.original.id,
              });
              setSelectedRunId(info.row.original.id);
            }}
          >
            <Icon className="lucide--alert-triangle mr-1 h-3 w-3" />
            {formatNumber(count)} pages
            <Icon className="lucide--arrow-right ml-1 h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.25" />
          </Button>
        ) : (
          <TableNullField />
        );
      },
    }),
  ];

  const table = useReactTable({
    data: runs ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  if (isLoading) {
    return (
      <>
        <div className="my-2 flex items-center gap-1.5">
          <Icon className="lucide--info size-3 text-gray-500" />
          <p className="text-xs text-gray-600">
            Runs happen when Wexler processes your documents (e.g. extracts facts from new documents, or assesses
            relevance of facts when creating a subset).
          </p>
        </div>
        <TableComponent>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Subset</TableHead>
              <TableHead>Submitted By</TableHead>
              <TableHead>Processed</TableHead>
              <TableHead>Failed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton className="h-4 w-28" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-36" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableComponent>
      </>
    );
  }

  if (!runs || runs.length === 0) {
    return <div className="py-12 text-center text-sm text-gray-500">No processing runs to display</div>;
  }

  return (
    <>
      <div className="my-2 flex items-center gap-1.5">
        <Icon className="lucide--info size-3 text-gray-500" />
        <p className="text-xs text-gray-600">
          Runs happen when Wexler processes your documents (e.g. extracts facts from new documents, or assesses
          relevance of facts when creating a subset).
        </p>
      </div>
      <TableComponent>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableComponent>
      {runs.length > 0 && <TablePagination table={table} plural="runs" />}
      {selectedRunId && (
        <FailedDocsModal
          open={!!selectedRunId}
          onOpenChange={(open) => !open && setSelectedRunId(null)}
          matterId={matterId}
          runId={selectedRunId}
        />
      )}

      <Dialog
        open={!!editingRun}
        onOpenChange={(open) => {
          if (!open) {
            setEditingRun(null);
            runNameForm.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Run Name</DialogTitle>
            <DialogDescription />
          </DialogHeader>

          <Form {...runNameForm}>
            <form onSubmit={runNameForm.handleSubmit(handleSaveName)}>
              <FormField
                control={runNameForm.control}
                name="runName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Run Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Initial document batch" autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setEditingRun(null);
                    runNameForm.reset();
                  }}
                  disabled={isUpdatingName}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isUpdatingName}>
                  {isUpdatingName ? (
                    <>
                      <Icon className="lucide--loader-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
